import { useCallback, useEffect, useState } from 'react'
import { api, ApiFehler } from './api'
import { Anmeldung } from './bereiche/Anmeldung'
import { EinzelneNotiz } from './bereiche/EinzelneNotiz'
import { Fragen } from './bereiche/Fragen'
import { Freunde } from './bereiche/Freunde'
import { Geteilt } from './bereiche/Geteilt'
import { Konto } from './bereiche/Konto'
import { MailLink } from './bereiche/MailLink'
import { Personen } from './bereiche/Personen'
import { Tagebuch } from './bereiche/Tagebuch'
import { Termine } from './bereiche/Termine'
import { Verwaltung } from './bereiche/Verwaltung'
import { bereiche, mailLinkAusHash, ortAusHash, type MailLinkDaten, type Ort } from './navigation'

const AUFNEHMEN_HASH = (hash: string) => ortAusHash(hash).pfad[0] === 'aufnehmen'

/** #aufnehmen ist ein Kurzlink zum Tagebuch; die Adresse wird gleich auf #tagebuch gesetzt, damit ein Neuladen nicht erneut aufnimmt */
function ortOhneAufnehmen(hash: string): Ort {
  if (!AUFNEHMEN_HASH(hash)) return ortAusHash(hash)
  window.history.replaceState(null, '', window.location.pathname + '#tagebuch')
  return { bereich: 'tagebuch', pfad: [] }
}

/** Liest einen Mail-Link aus der Adresse und entfernt das Token sofort daraus (Verlauf, Lesezeichen). */
function mailLinkUebernehmen(): MailLinkDaten | null {
  const link = mailLinkAusHash(window.location.hash)
  if (link) window.history.replaceState(null, '', window.location.pathname + '#tagebuch')
  return link
}


export function App() {
  const [benutzer, setBenutzer] = useState<string | null | undefined>(undefined)
  const [admin, setAdmin] = useState(false)
  const [mailLink, setMailLink] = useState<MailLinkDaten | null>(mailLinkUebernehmen)
  // Beim Start über #aufnehmen vor dem Bereinigen der Adresse merken, dass sofort aufgenommen werden soll
  const [sofortAufnehmen, setSofortAufnehmen] = useState(() => AUFNEHMEN_HASH(window.location.hash))
  const [ort, setOrt] = useState<Ort>(() => ortOhneAufnehmen(window.location.hash))
  const [status, setStatus] = useState({ kiBereit: true, rueckfragen: 0, terminvorschlaege: 0, freundschaftsanfragen: 0, neueGeteilte: 0, faelligeErinnerungen: 0 })

  /** KI-Status und Zaehler fuer die Badges (Rueckfragen, Terminvorschlaege) */
  const statusLaden = useCallback(() => {
    api.status().then(setStatus).catch(() => {})
  }, [])

  const pruefen = () =>
    api
      .ich()
      .then((i) => {
        setBenutzer(i.benutzername)
        setAdmin(i.admin)
        statusLaden()
      })
      .catch((e) => {
        if (e instanceof ApiFehler && e.status === 401) setBenutzer(null)
        else throw e
      })

  useEffect(() => {
    pruefen()
    const onHash = () => {
      const link = mailLinkUebernehmen()
      if (link) return setMailLink(link)
      if (AUFNEHMEN_HASH(window.location.hash)) setSofortAufnehmen(true)
      setOrt(ortOhneAufnehmen(window.location.hash))
      window.scrollTo(0, 0)
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // Rueckfragen und Terminvorschlaege entstehen im Hintergrund, sobald Notizen ausgewertet sind
  useEffect(() => {
    if (!benutzer) return
    const t = setInterval(statusLaden, 15000)
    return () => clearInterval(t)
  }, [benutzer, statusLaden])

  const zaehler: Partial<Record<string, { anzahl: number; text: string }>> = {
    tagebuch: { anzahl: status.neueGeteilte, text: 'neue geteilte Notizen' },
    personen: { anzahl: status.rueckfragen, text: 'offene Rückfragen' },
    termine: { anzahl: status.terminvorschlaege + status.faelligeErinnerungen, text: 'neue Terminvorschläge und fällige Erinnerungen' },
  }

  // Links aus Mails funktionieren ohne Anmeldung
  if (mailLink)
    return (
      <MailLink
        link={mailLink}
        onFertig={() => {
          setMailLink(null)
          pruefen()
        }}
      />
    )
  if (benutzer === undefined) return null
  if (benutzer === null) return <Anmeldung onAngemeldet={pruefen} />

  const titel = { konto: 'Konto', verwaltung: 'Benutzer', freunde: 'Freunde', geteilt: 'Von Freunden' }[ort.bereich as string] ?? bereiche.find((b) => b.id === ort.bereich)?.titel
  return (
    <div className="app">
      <header>
        <h1>{titel}</h1>
        <a className="konto-link" href={status.freundschaftsanfragen ? '#freunde' : '#konto'} aria-label="Konto">
          {benutzer} ⚙
          {!!status.freundschaftsanfragen && (
            <em className="zaehler" aria-label={`${status.freundschaftsanfragen} offene Freundschaftsanfragen`}>
              {status.freundschaftsanfragen}
            </em>
          )}
        </a>
      </header>
      <main>
        {ort.bereich === 'tagebuch' &&
          (ort.pfad[0] === 'notiz' && Number(ort.pfad[1]) ? (
            <EinzelneNotiz key={ort.pfad[1]} id={Number(ort.pfad[1])} kiBereit={status.kiBereit} onRueckfragen={statusLaden} />
          ) : (
            <Tagebuch
              kiBereit={status.kiBereit}
              pfad={ort.pfad}
              neueGeteilte={status.neueGeteilte}
              onAusgewertet={statusLaden}
              sofortAufnehmen={sofortAufnehmen}
              onAufnehmenGestartet={() => setSofortAufnehmen(false)}
            />
          ))}
        {ort.bereich === 'personen' && <Personen pfad={ort.pfad} onRueckfragenGeaendert={statusLaden} />}
        {ort.bereich === 'termine' && <Termine onGeaendert={statusLaden} />}
        {ort.bereich === 'fragen' && <Fragen />}
        {ort.bereich === 'konto' && <Konto benutzername={benutzer} admin={admin} onAbgemeldet={() => setBenutzer(null)} />}
        {ort.bereich === 'freunde' && <Freunde onGeaendert={statusLaden} />}
        {ort.bereich === 'geteilt' && <Geteilt key={ort.pfad[0] ?? ''} id={Number(ort.pfad[0]) || null} onGesehen={statusLaden} />}
        {ort.bereich === 'verwaltung' && (admin ? <Verwaltung ich={benutzer} /> : <p className="hinweis">Nur für Administratoren.</p>)}
      </main>
      <nav className="leiste">
        {bereiche.map((b) => (
          <a key={b.id} href={`#${b.id}`} className={b.id === ort.bereich ? 'aktiv' : undefined}>
            <span aria-hidden="true">{b.symbol}</span>
            {b.titel}
            {!!zaehler[b.id]?.anzahl && (
              <em className="zaehler" aria-label={`${zaehler[b.id]!.anzahl} ${zaehler[b.id]!.text}`}>
                {zaehler[b.id]!.anzahl}
              </em>
            )}
          </a>
        ))}
      </nav>
    </div>
  )
}
